/**
 * Branded identity of connector-next database rows as seen by the UI.
 *
 * These are deliberately distinct from the client-local branded ids in
 * `~/domain/changes/ids.ts` (fresh UUIDs minted per session) and from DICOM
 * UIDs: a `ConnectorDatasetId` names a row in the connector's patient
 * database, and is the only identity the save endpoints accept.
 */
export type ConnectorPatientId = string & {
    readonly __brand: 'ConnectorPatientId';
};

export type ConnectorDatasetId = string & {
    readonly __brand: 'ConnectorDatasetId';
};

/**
 * Opaque optimistic-concurrency token (the row's `UpdatedAt` ticks rendered
 * as a string — it exceeds `Number.MAX_SAFE_INTEGER`, so it must never be
 * parsed as a number). Captured at load time and echoed back verbatim in an
 * `If-Match` header on every save; a stale token is how the backend detects
 * that someone else changed the data since it was loaded (409).
 */
export type ConnectorVersionToken = string & {
    readonly __brand: 'ConnectorVersionToken';
};

/** Cast a wire string to its branded form at the DTO → domain boundary. */
export const asConnectorPatientId = (s: string): ConnectorPatientId =>
    s as ConnectorPatientId;
export const asConnectorDatasetId = (s: string): ConnectorDatasetId =>
    s as ConnectorDatasetId;
export const asConnectorVersionToken = (s: string): ConnectorVersionToken =>
    s as ConnectorVersionToken;

/**
 * Where a loaded dataset (structure set, registration, image, dose) lives in
 * the connector database. Absence of a ref means "local only" — the object
 * exists purely in this session (local files, or created in the UI and not
 * yet saved).
 */
export interface DatasetPersistenceRef {
    readonly datasetId: ConnectorDatasetId;
    /** Replaced (immutably) after each successful save. */
    readonly version: ConnectorVersionToken;
    /**
     * Present when the token was ADOPTED from a row that predates the
     * request that stored it — read live off the server, never earned by a
     * write this session made or a state it verified. An unacknowledged
     * token must not be used as the If-Match of a write: it is current by
     * construction, so the write would succeed and silently replace
     * another session's data without a conflict. Guarded paths either
     * re-verify the row against the sealed data or require the user's
     * explicit overwrite before writing; any acknowledged write replaces
     * the ref through updateVersion, which drops the marker.
     */
    readonly unacknowledgedVersion?: true;
}

/** @deprecated Alias kept while call sites migrate to DatasetPersistenceRef. */
export type StructureSetPersistenceRef = DatasetPersistenceRef;

/** Where the loaded patient lives in the connector database. */
export interface PatientPersistenceRef {
    readonly patientId: ConnectorPatientId;
    /**
     * The DICOM PatientID of the database row. Used only to refuse
     * hydration when the manifest and the parsed DICOM disagree about
     * which patient this is; never logged.
     */
    readonly dicomPatientId: string;
    readonly version: ConnectorVersionToken;
}
